import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import api from "../../../api";
import { useTranslation } from "../../../hooks/useTranslation";
import Register from "../../Register/Register";
import { isBusinessUser } from "../../../utils/postLoginPath";

const ConsumerSignInView = ({ onLoginSuccess, onGoHome, initialMode = "login" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode); // 'login' | 'register'
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Register.js is a controlled component — this state block mirrors the
  // wiring AuthPopup.js used to drive it.
  const [registrationStep, setRegistrationStep] = useState(1);
  const [name, setName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");
  const [birthMonth, setBirthMonth] = useState("");
  const [birthDay, setBirthDay] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [acceptMarketing, setAcceptMarketing] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationSent, setVerificationSent] = useState(false);

  const handleRegisterSuccess = (userData) => {
    onLoginSuccess(userData);
    isBusinessUser(userData) ? navigate("/admin") : onGoHome();
  };

  const handleSignin = async (e) => {
    e.preventDefault();
    setError("");
    setResetMessage("");
    setSubmitting(true);
    const password = e.target.password.value;
    try {
      const response = await api.login(email, password, false);
      onLoginSuccess(response.data.user);
      isBusinessUser(response.data.user) ? navigate("/admin") : onGoHome();
    } catch (err) {
      if (err.response?.status === 429) {
        setError(t.tooManyLoginAttempts);
      } else {
        setError(t.landingSignInError);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError("");
    setResetMessage("");
    if (!email) {
      setError(t.landingForgotNoEmail);
      return;
    }
    try {
      await api.requestPasswordReset(email);
      setResetMessage(t.landingForgotSent);
    } catch (err) {
      setError(err.response?.data?.message || t.landingForgotError);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError("");
    try {
      const response = await api.googleLogin(credentialResponse.credential);
      onLoginSuccess(response.data.user);
      isBusinessUser(response.data.user) ? navigate("/admin") : onGoHome();
    } catch (err) {
      if (err.response?.status === 429) {
        setError(t.tooManyLoginAttempts);
      } else {
        setError(t.landingGoogleError);
      }
    }
  };

  return (
    <main
      style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "60px 32px",
        minHeight: "calc(100vh - 64px)",
      }}
    >
      <div style={{ width: "100%", maxWidth: mode === "register" ? 480 : 380 }}>
        {mode === "login" ? (
          <>
            <div className="tag tag-accent">{t.landingSignInKicker}</div>
            <h1 style={{ fontSize: 34, marginTop: 12 }}>{t.landingSignInHeading}</h1>
            <p style={{ opacity: 0.8 }}>{t.landingSignInSubtitle}</p>
            <form style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 20 }} onSubmit={handleSignin}>
              <div className="field">
                <label>{t.landingBizPortalEmailLabel}</label>
                <input
                  className="input"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="field">
                <label>{t.landingBizPortalPasswordLabel}</label>
                <input className="input" name="password" type="password" required />
              </div>
              <a
                href="#forgot"
                className="btn-ghost"
                style={{ alignSelf: "flex-end", fontSize: 13, textDecoration: "none" }}
                onClick={handleForgotPassword}
              >
                {t.landingBizPortalForgot}
              </a>
              <button type="submit" className="btn btn-primary btn-block" style={{ padding: 12 }} disabled={submitting}>
                {t.landingSignInLoginBtn}
              </button>
            </form>
            {error && (
              <p style={{ marginTop: 12, fontSize: 13, color: "var(--color-accent-700)" }}>{error}</p>
            )}
            {resetMessage && (
              <p style={{ marginTop: 12, fontSize: 13, color: "var(--color-accent-2-700)" }}>{resetMessage}</p>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "20px 0" }}>
              <div className="hr" style={{ flex: 1, margin: 0 }} />
              <span style={{ fontSize: 12, opacity: 0.6 }}>{t.landingOrDivider}</span>
              <div className="hr" style={{ flex: 1, margin: 0 }} />
            </div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setError(t.landingGoogleError)}
                width={300}
              />
            </div>
            <p style={{ marginTop: 20, fontSize: 13, textAlign: "center" }}>
              {t.landingSignInNoAccount}{" "}
              <button
                type="button"
                className="btn-ghost"
                style={{ color: "var(--color-accent-700)", background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }}
                onClick={() => setMode("register")}
              >
                {t.landingSignInCreateAccount}
              </button>
            </p>
          </>
        ) : (
          <>
            <div className="tag tag-accent">{t.landingSignUpKicker}</div>
            <Register
              onRegisterSuccess={handleRegisterSuccess}
              onRegistrationComplete={onGoHome}
              onShowTerms={() => {}}
              onSwitchToLogin={() => setMode("login")}
              step={registrationStep}
              setStep={setRegistrationStep}
              name={name}
              setName={setName}
              firstName={firstName}
              setFirstName={setFirstName}
              lastName={lastName}
              setLastName={setLastName}
              email={regEmail}
              setEmail={setRegEmail}
              confirmEmail={confirmEmail}
              setConfirmEmail={setConfirmEmail}
              birthMonth={birthMonth}
              setBirthMonth={setBirthMonth}
              birthDay={birthDay}
              setBirthDay={setBirthDay}
              birthYear={birthYear}
              setBirthYear={setBirthYear}
              password={password}
              setPassword={setPassword}
              repeatPassword={repeatPassword}
              setRepeatPassword={setRepeatPassword}
              acceptMarketing={acceptMarketing}
              setAcceptMarketing={setAcceptMarketing}
              verificationCode={verificationCode}
              setVerificationCode={setVerificationCode}
              verificationSent={verificationSent}
              setVerificationSent={setVerificationSent}
            />
            <p style={{ marginTop: 20, fontSize: 13, textAlign: "center" }}>
              {t.landingSignInHaveAccount}{" "}
              <button
                type="button"
                className="btn-ghost"
                style={{ color: "var(--color-accent-700)", background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit" }}
                onClick={() => setMode("login")}
              >
                {t.landingSignInBackToLogin}
              </button>
            </p>
          </>
        )}
      </div>
    </main>
  );
};

export default ConsumerSignInView;
